import React, { createContext, useState, useContext, ReactNode, useEffect,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { jwtDecode,JwtPayload } from 'jwt-decode';

// Define the shape of your context

const SECRET_KEY = CryptoJS.enc.Utf8.parse('12345678901234567890123456789012');

const backendUrl = process.env.REACT_APP_BACKEND_URL

export const encryptData = (data: any) => {
  if (!data) {
    //console.error('Invalid data passed for encryption.');
    return '';
  }

  try {
    // Convert data to a string if it's not already
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data);

    const encrypted = CryptoJS.AES.encrypt(dataStr.trim(), SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch (error) {
    //console.error('Encryption failed:', error);
    return '';
  }
};

export const decryptData = (encryptedData: any) => {
  if (!encryptedData) {
    //console.error('Invalid data passed for decryption.');
    return null;
  }

  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    //console.error('Decryption failed:', error);
    return null;
  }
};
interface UserContextType {
  user_id: string | null;
  user_name: string | null;
  auth_token: string | null;
  city_name: string | null;
  utm_session_id: string | null;
  sess_code: string | null;
  globalSearchTerm: string;
  globalAddressErrors: {
    address_1: string;
    address_1_error_status: boolean;
    address_2: string;
    address_2_error_status: boolean;
    landmark: string;
    landmark_error_status: boolean;
  };
  globalUtmQueryRef: any;
  setUserId: (id: string | null) => void;
  setUsername: (name: string | null) => void;
  setAuthToken: (auth_token: string | null) => void;
  setcityName: (city_name: string | null) => void;
  setSessionCode: (sess_code: string) => void;
  setUtmSessionId: (utm_session_id: string | null) => void;
  checkSessionTokenExpirationStatus: () => boolean;
  handleRemoveLocalStorage: () => void;
  fetchCsrfToken: () => any;
  setGlobalSearchTerm: (globalSearchTerm: string) => void;
  handleAddressErrorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Create the context with default values
const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  //const navigate = useNavigate();
  // Initialize state with values from localStorage if they exist
  const [user_id, setUserId] = useState<string | null>(
    decryptData(localStorage.getItem("user_id") || '') || null
  );
  const [user_name, setUsername] = useState<string | null>(
    localStorage.getItem("user_name")
  );
  const [auth_token, setAuthToken] = useState<string | null>(
    localStorage.getItem("auth_token")
  );
  const [city_name, setcityName] = useState<string | null>(
    localStorage.getItem("city_name")
  );
  const [sess_code, setSessionCode] = useState<string | null>(
    localStorage.getItem("sess_code")
  );
  const [utm_session_id, setUtmSessionId] = useState<string | null>(
    localStorage.getItem("utm_session_id")
  );
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>("")
  const [globalAddressErrors, setGlobalAddressErrors] = useState({
    address_1: "",
    address_1_error_status:false,
    address_2: "",
    address_2_error_status:false,
    landmark: "",
    landmark_error_status:false,
  })
  let globalUtmQueryRef = useRef<any>("")
 
  useEffect(() => {
    if (user_id) {
      localStorage.setItem("user_id", encryptData(user_id));
    } else {
      localStorage.removeItem("user_id");
    }
  }, [user_id]);

  useEffect(() => {
    if (user_name) {
      localStorage.setItem("user_name", user_name);
    } else {
      localStorage.removeItem("user_name");
    }
  }, [user_name]);

  useEffect(() => {
    if (auth_token) {
      localStorage.setItem("auth_token", auth_token);
      localStorage.setItem(
        "auth_token_timestamp",
        new Date().getTime().toString()
      ); // Save the current timestamp
    } else {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_token_timestamp"); // Also remove timestamp
    }
  }, [auth_token]);

  useEffect(() => {
    if (city_name) {
      localStorage.setItem("city_name", city_name);
    } else {
      localStorage.removeItem("city_name");
    }
  }, [city_name]);

  useEffect(() => {

    if (sess_code) {
      localStorage.setItem("sess_code", sess_code);
    } else {
      localStorage.removeItem("sess_code");
    }
  }, [sess_code]);

  useEffect(() => {
    if (utm_session_id) {
      localStorage.setItem("utm_session_id", utm_session_id);
    } else {
      localStorage.removeItem("utm_session_id");
    }
  }, [utm_session_id]);

  const handleRemoveLocalStorage = () => {
    localStorage.clear()
    sessionStorage.clear()
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userName");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("city_name");
    localStorage.removeItem("cartQuantity");
    localStorage.removeItem("sess_code");
      sessionStorage.removeItem("session_code");
      sessionStorage.removeItem("user_id");
      sessionStorage.removeItem("cartQuantity");
      sessionStorage.clear()
        localStorage.clear()
  };

  const fetchCsrfToken = async  () => {
    const response = await fetch(`${backendUrl}api/v1/csrf-token`,
      {  method: 'GET',
        headers:{
          // Accept:'aplication/json',
          "Content-Type": "application/json",
  
        },
        credentials: "include",
        mode:"cors"
  
      }
    );
    const data = await response.json();
    return data.csrfToken;
  };

  const getTokenExpirationTime = (token: string): number | null => {
    try {
      const decodedToken = jwtDecode<JwtPayload & { exp?: number }>(token);

      if (decodedToken.exp) {
        return decodedToken.exp * 1000;
      }
      return null;
    } catch (error) {
      //console.error("Invalid token:", error);
      return null;
    }
  };

    const checkSessionTokenExpirationStatus = () => {
  
        let auth_token = localStorage.getItem("auth_token") as string;
        let auth_token_exp = getTokenExpirationTime(auth_token);
        // //console.log("auth_token_exp timestamp", auth_token_exp, new Date().getTime());
        if (auth_token_exp && auth_token_exp <= new Date().getTime()) {
            // //console.log("session token expired functionality triggered");
            return true
        } else {
            // //console.log("session token expired functionality not triggered");
            return false
        }
    }
    
  const handleAddressErrorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let updatedValue = value.trimStart();
    //console.log("handleAddressErrorChange ",value);
    let lengthError = "Please enter at least 2 characters"
    switch (name) {
      case "address_1":
       // Check if the value is empty or contains only spaces
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_1: "",
            address_1_error_status: false,
          })); // Clear errors if valid
        }
          else if (updatedValue.length < 2) {
            setGlobalAddressErrors((prev) => ({
              ...prev,
              address_1: lengthError,
              address_1_error_status: true,
            }));
          } else {
            setGlobalAddressErrors((prev) => ({
              ...prev,
              address_1: "",
              address_1_error_status: false,
            })); // Clear errors if valid
          }
        break;
      case "address_2":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: "",
            address_2_error_status: false,
          })); // Clear errors if valid
        }
        else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: lengthError,
            address_2_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: "",
            address_2_error_status: false,
          })); // Clear errors if valid
        }
        break;
        break;

      case "landmark":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: "",
            landmark_error_status: false,
          })); // Clear errors if valid
        }
        else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: lengthError,
            landmark_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: "",
            landmark_error_status: false,
          })); // Clear errors if valid
        }

      default:
        break;
    }
  };

  useEffect(() => {
    const handlePopState = (event:any) => {
      // force reload in home page
      if(window.location.pathname === "/") window.location.reload()

    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
    
 

  return (
    <UserContext.Provider
      value={{
        user_id,
        user_name,
        auth_token,
        city_name,
        utm_session_id,
        sess_code,
        globalSearchTerm,
        globalAddressErrors,
        globalUtmQueryRef,
        setUserId,
        setUsername,
        setAuthToken,
        setcityName,
        setUtmSessionId,
        setSessionCode,
        checkSessionTokenExpirationStatus,
        handleRemoveLocalStorage,
       fetchCsrfToken,
        setGlobalSearchTerm,
        handleAddressErrorChange,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
